import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { DriverIntegration, GratyfikantMapping } from '@/drivers/drivers-integration/_models/drivers-integration.model';
import {
	BonusExclusionInfo,
	BonusExclusionPayload,
	DriverTourismEvent,
	EventExclusionPayload,
	EventExclusionResponse,
	GratyfikantSalariesResponse,
	NavbridgeBonusesStats,
	NawigatorPullStats,
	SalaryCacheResponse,
	SalaryNotesPayload,
	SalaryOverridesPayload,
} from '../_models/drivers-salary-v2.model';

@Injectable({
	providedIn: 'root',
})
export class DriversSalaryV2ApiService {
	private readonly http = inject(HttpClient);
	private readonly apiUrl = environment.erpSymfonyUrl;

	getGratyfikantSalaries(driverId: string, month: string): Observable<GratyfikantSalariesResponse> {
		return this.http.get<GratyfikantSalariesResponse>(
			`${this.apiUrl}/driver/${driverId}/integration/gratyfikant/salaries`,
			{ params: { month } },
		);
	}

	getGratyfikantSalariesRaw(driverId: string): Observable<GratyfikantSalariesResponse> {
		return this.http.get<GratyfikantSalariesResponse>(
			`${this.apiUrl}/driver/${driverId}/integration/gratyfikant/salaries`,
		);
	}

	pullNawigatorEvents(driverId: string, month: string): Observable<NawigatorPullStats> {
		return this.http
			.get<{
				data: { stats: NawigatorPullStats };
			}>(`${this.apiUrl}/driver/${driverId}/integration/nawigator/events`, { params: { month } })
			.pipe(map((response) => response.data.stats));
	}

	pullNavbridgeBonuses(driverId: string, month: string): Observable<NavbridgeBonusesStats> {
		return this.http
			.get<{
				data: { stats: NavbridgeBonusesStats };
			}>(`${this.apiUrl}/driver/${driverId}/integration/navbridge/bonuses`, { params: { month } })
			.pipe(map((response) => response.data.stats));
	}

	getEnovaSalaries(driverId: string, month: string): Observable<Record<string, unknown>[]> {
		return this.http
			.get<{ data: Record<string, unknown>[] }>(`${this.apiUrl}/driver/${driverId}/integration/enova/salaries`, {
				params: { month },
			})
			.pipe(map((response) => response.data));
	}

	getSalaryCache(driverId: string, month: string): Observable<SalaryCacheResponse | null> {
		return this.http
			.get<{ data: SalaryCacheResponse | null }>(`${this.apiUrl}/driver/${driverId}/salary`, {
				params: { month },
			})
			.pipe(map((response) => response.data));
	}

	saveNotes(driverId: string, month: string, payload: SalaryNotesPayload): Observable<SalaryCacheResponse> {
		return this.http
			.put<{ data: SalaryCacheResponse }>(`${this.apiUrl}/driver/${driverId}/salary/notes`, payload, {
				params: { month },
			})
			.pipe(map((response) => response.data));
	}

	saveOverrides(driverId: string, month: string, payload: SalaryOverridesPayload): Observable<SalaryCacheResponse> {
		return this.http
			.put<{ data: SalaryCacheResponse }>(`${this.apiUrl}/driver/${driverId}/salary/overrides`, payload, {
				params: { month },
			})
			.pipe(map((response) => response.data));
	}

	settleMonth(driverId: string, month: string): Observable<SalaryCacheResponse> {
		return this.http
			.post<{ data: SalaryCacheResponse }>(`${this.apiUrl}/driver/${driverId}/salary/settle`, null, {
				params: { month },
			})
			.pipe(map((response) => response.data));
	}

	unsettleMonth(driverId: string, month: string): Observable<SalaryCacheResponse | null> {
		return this.http
			.delete<{ data: SalaryCacheResponse } | null>(`${this.apiUrl}/driver/${driverId}/salary/settle`, {
				params: { month },
			})
			.pipe(map((response) => response?.data ?? null));
	}

	getTourismEvents(driverId: string, month: string): Observable<DriverTourismEvent[]> {
		return this.http
			.get<{ data: DriverTourismEvent[] }>(`${this.apiUrl}/driver/${driverId}/tourism-events`, {
				params: { month },
			})
			.pipe(map((response) => response.data));
	}

	setEventExclusion(
		driverId: string,
		eventId: string,
		payload: EventExclusionPayload,
	): Observable<EventExclusionResponse> {
		return this.http
			.patch<{
				data: EventExclusionResponse;
			}>(`${this.apiUrl}/driver/${driverId}/tourism-events/${eventId}/settlement-exclusion`, payload)
			.pipe(map((response) => response.data));
	}

	getSettlementBonusExclusions(driverId: string): Observable<BonusExclusionInfo[]> {
		return this.http
			.get<{ data: BonusExclusionInfo[] }>(`${this.apiUrl}/driver/${driverId}/settlement-bonuses/exclusions`)
			.pipe(map((response) => response.data));
	}

	setBonusExclusion(
		driverId: string,
		bonusId: string,
		payload: BonusExclusionPayload,
	): Observable<BonusExclusionInfo> {
		return this.http
			.patch<{
				data: BonusExclusionInfo;
			}>(`${this.apiUrl}/driver/${driverId}/settlement-bonuses/${bonusId}/settlement-exclusion`, payload)
			.pipe(map((response) => response.data));
	}

	getIntegration(driverId: string): Observable<DriverIntegration> {
		return this.http
			.get<{ data: DriverIntegration }>(`${this.apiUrl}/driver/${driverId}/integration`)
			.pipe(map((response) => response.data));
	}

	getGratyfikantMappings(driverId: string): Observable<GratyfikantMapping[]> {
		return this.http
			.get<{ data: GratyfikantMapping[] }>(`${this.apiUrl}/driver/${driverId}/integration/gratyfikant`)
			.pipe(map((response) => response.data));
	}
}
